/**
 * Group / Facility portal — nursing homes, assisted living, day programs.
 * Signed-in facilities can:
 *   · Manage a roster of patients (each gets their own patient login too)
 *   · Bulk-book rides: "same trip" for a group activity OR "different trip per row"
 *   · See all facility rides in one dashboard
 */
import { useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import {
  Building2, LogOut, Plus, Users, Loader2, Calendar, MapPin, Trash2, XCircle, ChevronRight, Check,
} from "lucide-react";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function req(path, opts = {}) {
  const res = await fetch(`${API}/group${path}`, { credentials: "include", ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Server ${res.status}`);
  return data;
}

const titleCase = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Group() {
  const [group, setGroup] = useState(null);
  const [mode, setMode] = useState("login"); // login | register
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const d = await req("/me");
        setGroup(d.group);
      } catch (_) { /* not logged in */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-brand-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg">
      <Toaster position="top-center" richColors />
      {group
        ? <Dashboard group={group} onLogout={() => { req("/auth/logout", { method: "POST" }); setGroup(null); }} />
        : <SignInCard mode={mode} setMode={setMode} onSuccess={setGroup} />
      }
    </div>
  );
}

/* ==================== Sign in / register ==================== */

function SignInCard({ mode, setMode, onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orgName, setOrgName] = useState("");
  const [groupType, setGroupType] = useState("nursing_home");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = mode === "login"
        ? { email, password }
        : { email, password, org_name: orgName, group_type: groupType,
            contact_name: contactName, phone, address };
      const d = await req(`/auth/${mode === "login" ? "login" : "register"}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      onSuccess(d.group);
      toast.success(mode === "login" ? `Welcome back, ${d.group.org_name}` : "Facility account created");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-brand-deep via-brand-navy to-brand-navyDark">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex justify-center mb-6">
          <Logo variant="dark" size="md" />
        </div>
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">
            <Building2 className="w-3 h-3" /> Facility portal
          </div>
          <h1 className="font-serif text-3xl text-brand-deep mt-2">
            {mode === "login" ? "Sign in to your facility" : "Register your facility"}
          </h1>
          <p className="text-sm text-brand-muted mt-2">
            Manage your residents&rsquo; medical transportation in one place.
          </p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <>
              <input value={orgName} onChange={(e) => setOrgName(e.target.value)} required
                     placeholder="Facility name" data-testid="group-org-name"
                     className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green" />
              <select value={groupType} onChange={(e) => setGroupType(e.target.value)}
                      data-testid="group-type"
                      className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15">
                <option value="nursing_home">Nursing home</option>
                <option value="assisted_living">Assisted living</option>
                <option value="day_program">Day program / activities</option>
                <option value="group_home">Group home</option>
                <option value="other">Other</option>
              </select>
              <input value={contactName} onChange={(e) => setContactName(e.target.value)} required
                     placeholder="Contact person" data-testid="group-contact-name"
                     className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
              <input value={phone} onChange={(e) => setPhone(e.target.value)} required
                     placeholder="Contact phone" data-testid="group-phone"
                     className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
              <input value={address} onChange={(e) => setAddress(e.target.value)}
                     placeholder="Facility address (optional)" data-testid="group-address"
                     className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
            </>
          )}
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                 placeholder="Email" data-testid="group-email"
                 className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
                 placeholder={mode === "login" ? "Password" : "Create password (min 8)"} data-testid="group-password"
                 className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
          <button type="submit" disabled={busy} data-testid="group-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-medium py-3 rounded-full disabled:opacity-60 transition">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>
        <button
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          data-testid="group-toggle-mode"
          className="mt-4 w-full text-xs text-brand-muted hover:text-brand-navy"
        >
          {mode === "login" ? "New facility? Register →" : "Already have an account? Sign in →"}
        </button>
      </div>
    </div>
  );
}

/* ==================== Dashboard ==================== */

function Dashboard({ group, onLogout }) {
  const [tab, setTab] = useState("patients"); // patients | book | rides
  const [patients, setPatients] = useState([]);
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showBook, setShowBook] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([req("/patients"), req("/rides")]);
      setPatients(p.patients || []);
      setRides(r.rides || []);
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <header className="bg-white border-b border-brand-navy/10 px-5 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-brand-green" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Facility portal</div>
            <div className="font-serif text-lg text-brand-deep leading-tight">{group.org_name}</div>
          </div>
        </div>
        <button onClick={onLogout} data-testid="group-logout" className="inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand-navy">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </header>

      <nav className="bg-white border-b border-brand-navy/10 px-5 md:px-8 flex gap-1 overflow-x-auto">
        {[
          { k: "patients", label: `Patients (${patients.length})` },
          { k: "book", label: "Book rides" },
          { k: "rides", label: `Rides (${rides.length})` },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`group-tab-${t.k}`}
                  className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition ${
                    tab === t.k ? "border-brand-green text-brand-green" : "border-transparent text-brand-muted hover:text-brand-navy"
                  }`}>
            {t.label}
          </button>
        ))}
      </nav>

      <main className="max-w-5xl mx-auto p-5 md:p-8">
        {loading && (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-brand-green" /></div>
        )}

        {!loading && tab === "patients" && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl text-brand-deep">Roster</h2>
              <button onClick={() => setShowAdd(true)} data-testid="add-patient-btn"
                      className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white text-sm font-medium px-4 py-2.5 rounded-full">
                <Plus className="w-4 h-4" /> Add patient
              </button>
            </div>
            {patients.length === 0 ? (
              <div className="bg-white rounded-2xl border border-brand-navy/10 p-8 text-center text-brand-muted">
                <Users className="w-8 h-8 mx-auto mb-3 text-brand-green" />
                No patients yet — click <strong>Add patient</strong> to add the first resident.
              </div>
            ) : (
              <div className="space-y-2" data-testid="patients-list">
                {patients.map((p) => <PatientRow key={p.id} p={p} onDeleted={load} />)}
              </div>
            )}
          </>
        )}

        {!loading && tab === "book" && (
          <BookRides patients={patients} onBooked={() => { setTab("rides"); load(); }} />
        )}

        {!loading && tab === "rides" && (
          <>
            <h2 className="font-serif text-2xl text-brand-deep mb-4">Rides ({rides.length})</h2>
            {rides.length === 0 ? (
              <div className="bg-white rounded-2xl border border-brand-navy/10 p-8 text-center text-brand-muted">
                No rides yet. <button className="text-brand-green underline" onClick={() => setTab("book")}>Book one →</button>
              </div>
            ) : (
              <div className="space-y-2" data-testid="rides-list">
                {rides.map((r) => (
                  <div key={r.id} className="bg-white rounded-2xl border border-brand-navy/10 p-4" data-testid={`ride-row-${r.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="font-medium text-brand-deep">{r.member_name}</div>
                      <div className="flex items-center gap-2">
                        {r.activity === "extracurricular" && (
                          <span className="text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">Facility outing</span>
                        )}
                        <span className="text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{r.status?.replace(/_/g, " ")}</span>
                      </div>
                    </div>
                    <div className="text-xs text-brand-muted font-mono mt-1">{r.trip_date} · {r.trip_time}</div>
                    <div className="text-xs text-brand-deep mt-1"><MapPin className="w-3 h-3 inline text-emerald-600 mr-1" /> {r.pickup}</div>
                    <div className="text-xs text-brand-muted mt-0.5">→ {r.dropoff}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {showAdd && <AddPatientModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {showBook && <div />}
    </div>
  );
}

function PatientRow({ p, onDeleted }) {
  const remove = async () => {
    if (!window.confirm(`Remove ${p.name} from this facility roster? Their patient account is preserved.`)) return;
    try {
      await req(`/patients/${p.id}`, { method: "DELETE" });
      toast.success("Removed from roster");
      onDeleted();
    } catch (err) { toast.error(err.message); }
  };
  return (
    <div className="bg-white rounded-2xl border border-brand-navy/10 p-4 flex items-center justify-between gap-3 flex-wrap"
         data-testid={`patient-row-${p.id}`}>
      <div>
        <div className="font-medium text-brand-deep">{p.name}</div>
        <div className="text-xs text-brand-muted mt-0.5">
          {p.phone} · {titleCase(p.payer_type || "private")} · {titleCase(p.mobility_default || "ambulatory")}
        </div>
        {p.email && <div className="text-[11px] text-brand-muted mt-0.5">{p.email}</div>}
      </div>
      <button onClick={remove} data-testid={`remove-patient-${p.id}`} className="text-red-600 hover:text-red-700 p-2" aria-label="Remove">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

/* ==================== Add-patient modal ==================== */

function AddPatientModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [payerType, setPayerType] = useState("private");
  const [mobility, setMobility] = useState("ambulatory");
  const [medicaidId, setMedicaidId] = useState("");
  const [dob, setDob] = useState("");
  const [homeAddress, setHomeAddress] = useState("");
  const [sendInvite, setSendInvite] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        name, phone, email: email || undefined, dob: dob || undefined,
        medicaid_id: medicaidId || undefined, payer_type: payerType,
        mobility, home_address: homeAddress || undefined, send_invite: sendInvite && !!email,
      };
      const d = await req("/patients", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      toast.success(d.reused ? "Patient linked to your facility" : "Patient added");
      onSaved();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="add-patient-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Add patient</div>
            <h3 className="font-serif text-2xl text-brand-deep mt-1">New resident</h3>
          </div>
          <button onClick={onClose} className="text-brand-muted hover:text-brand-navy"><XCircle className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Full name"
                 data-testid="ap-name" className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
          <input value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="Phone"
                 data-testid="ap-phone" className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (auto-creates patient login)"
                 data-testid="ap-email" className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
          <div className="grid grid-cols-2 gap-3">
            <select value={payerType} onChange={(e) => setPayerType(e.target.value)}
                    data-testid="ap-payer" className="px-4 py-2.5 rounded-lg border border-brand-navy/15">
              <option value="private">Private pay</option>
              <option value="medicaid">Medicaid</option>
              <option value="medicare_adv">Medicare Advantage</option>
            </select>
            <select value={mobility} onChange={(e) => setMobility(e.target.value)}
                    data-testid="ap-mobility" className="px-4 py-2.5 rounded-lg border border-brand-navy/15">
              <option value="ambulatory">Ambulatory</option>
              <option value="wheelchair">Wheelchair</option>
              <option value="stretcher">Stretcher</option>
              <option value="assist_only">Assist only</option>
            </select>
          </div>
          <input value={medicaidId} onChange={(e) => setMedicaidId(e.target.value)}
                 placeholder="Medicaid / member ID (optional)" data-testid="ap-medicaid-id"
                 className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
          <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} placeholder="DOB (optional)"
                 data-testid="ap-dob" className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
          <input value={homeAddress} onChange={(e) => setHomeAddress(e.target.value)}
                 placeholder="Home / facility address (optional)" data-testid="ap-home"
                 className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
          <label className="flex items-center gap-2 text-xs text-brand-muted">
            <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} data-testid="ap-send-invite" />
            Email the patient portal invite (requires email above)
          </label>
          <button type="submit" disabled={busy} data-testid="ap-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-medium py-3 rounded-full disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add patient
          </button>
        </form>
      </div>
    </div>
  );
}

/* ==================== Bulk-book ==================== */

function BookRides({ patients, onBooked }) {
  const [mode, setMode] = useState("same"); // same | different
  const [activity, setActivity] = useState("medical"); // medical | extracurricular
  const [selected, setSelected] = useState(new Set());
  const [shared, setShared] = useState({
    pickup: "", dropoff: "", trip_date: "", trip_time: "",
    trip_class: "local_oneway", mobility: "ambulatory", passengers: 1,
    round_trip: false, wait_minutes: 0, notes: "",
  });
  const [rows, setRows] = useState([]); // one per patient in "different" mode
  const [busy, setBusy] = useState(false);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
    // For "different" mode, keep the rows in sync
    setRows((prev) => {
      const kept = prev.filter((r) => s.has(r.patient_id));
      const missing = [...s].filter((id2) => !kept.find((r) => r.patient_id === id2));
      const patientsById = Object.fromEntries(patients.map((p) => [p.id, p]));
      const added = missing.map((pid) => ({
        patient_id: pid,
        pickup: patientsById[pid]?.home_address || "",
        dropoff: "", trip_date: "", trip_time: "",
        trip_class: "local_oneway", mobility: patientsById[pid]?.mobility_default || "ambulatory",
        passengers: 1, round_trip: false, wait_minutes: 0, notes: "",
      }));
      return [...kept, ...added];
    });
  };
  const updateRow = (pid, field, value) => {
    setRows((prev) => prev.map((r) => r.patient_id === pid ? { ...r, [field]: value } : r));
  };
  const submit = async () => {
    if (selected.size === 0) { toast.error("Pick at least one patient"); return; }
    setBusy(true);
    try {
      const body = mode === "same"
        ? { mode, activity, patient_ids: [...selected], shared }
        : { mode, activity, rides: rows };
      const d = await req("/rides/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      toast.success(`Created ${d.created} ride${d.created === 1 ? "" : "s"}`);
      onBooked();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <h2 className="font-serif text-2xl text-brand-deep">Book rides</h2>

      {/* Mode toggle */}
      <div className="bg-white rounded-2xl border border-brand-navy/10 p-5">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-3">Booking mode</div>
        <div className="flex flex-wrap gap-2">
          <ModeButton active={mode === "same"} onClick={() => setMode("same")} testid="mode-same"
                     label="Same trip for all" hint="Shuttle everyone to the same destination" />
          <ModeButton active={mode === "different"} onClick={() => setMode("different")} testid="mode-different"
                     label="Different trip per patient" hint="Each row has its own address / time" />
        </div>
        <div className="mt-4 pt-4 border-t border-brand-navy/10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-3">Purpose</div>
          <div className="flex flex-wrap gap-2">
            <ModeButton active={activity === "medical"} onClick={() => setActivity("medical")} testid="activity-medical"
                       label="Medical visit" hint="Bills each patient's payer" />
            <ModeButton active={activity === "extracurricular"} onClick={() => setActivity("extracurricular")} testid="activity-extra"
                       label="Facility outing" hint="Bills the facility (church, museum, etc.)" />
          </div>
        </div>
      </div>

      {/* Patient picker */}
      <div className="bg-white rounded-2xl border border-brand-navy/10 p-5">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-3">
          Select patients ({selected.size} selected)
        </div>
        {patients.length === 0 ? (
          <div className="text-sm text-brand-muted">Add patients to your roster first.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
            {patients.map((p) => {
              const isOn = selected.has(p.id);
              return (
                <label key={p.id} data-testid={`pick-${p.id}`}
                       className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer text-sm ${
                         isOn ? "border-brand-green bg-emerald-50" : "border-brand-navy/15"
                       }`}>
                  <input type="checkbox" checked={isOn} onChange={() => toggle(p.id)} className="accent-brand-green" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-brand-deep truncate">{p.name}</div>
                    <div className="text-[11px] text-brand-muted">{titleCase(p.payer_type || "private")} · {titleCase(p.mobility_default || "ambulatory")}</div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Same-mode single form */}
      {mode === "same" && selected.size > 0 && (
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Trip details (applies to all selected)</div>
          <TripFields v={shared} set={(patch) => setShared({ ...shared, ...patch })} prefix="shared" />
        </div>
      )}

      {/* Different-mode per-row */}
      {mode === "different" && rows.length > 0 && (
        <div className="space-y-3">
          {rows.map((r) => {
            const p = patients.find((x) => x.id === r.patient_id);
            return (
              <div key={r.patient_id} className="bg-white rounded-2xl border border-brand-navy/10 p-5 space-y-3"
                   data-testid={`row-${r.patient_id}`}>
                <div className="font-serif text-lg text-brand-deep">{p?.name}</div>
                <TripFields v={r} set={(patch) => Object.entries(patch).forEach(([k, val]) => updateRow(r.patient_id, k, val))}
                           prefix={`row-${r.patient_id}`} />
              </div>
            );
          })}
        </div>
      )}

      {selected.size > 0 && (
        <button onClick={submit} disabled={busy} data-testid="bulk-submit"
                className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-medium px-6 py-3 rounded-full disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Book {selected.size} ride{selected.size === 1 ? "" : "s"}
        </button>
      )}
    </div>
  );
}

function ModeButton({ active, onClick, label, hint, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className={`flex-1 min-w-[200px] text-left px-4 py-3 rounded-xl border-2 transition ${
              active ? "border-brand-green bg-emerald-50" : "border-brand-navy/15 hover:border-brand-navy/30"
            }`}>
      <div className="font-medium text-brand-deep text-sm">{label}</div>
      <div className="text-xs text-brand-muted mt-0.5">{hint}</div>
    </button>
  );
}

function TripFields({ v, set, prefix }) {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-brand-deep block mb-1">Pickup</label>
          <input value={v.pickup} onChange={(e) => set({ pickup: e.target.value })}
                 data-testid={`${prefix}-pickup`} className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
        </div>
        <div>
          <label className="text-xs font-medium text-brand-deep block mb-1">Destination</label>
          <input value={v.dropoff} onChange={(e) => set({ dropoff: e.target.value })}
                 data-testid={`${prefix}-dropoff`} className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
        </div>
        <div>
          <label className="text-xs font-medium text-brand-deep block mb-1">Date</label>
          <input type="date" value={v.trip_date} onChange={(e) => set({ trip_date: e.target.value })}
                 data-testid={`${prefix}-date`} className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
        </div>
        <div>
          <label className="text-xs font-medium text-brand-deep block mb-1">Time</label>
          <input type="time" value={v.trip_time} onChange={(e) => set({ trip_time: e.target.value })}
                 data-testid={`${prefix}-time`} className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
        </div>
        <div>
          <label className="text-xs font-medium text-brand-deep block mb-1">Trip type</label>
          <select value={v.trip_class} onChange={(e) => set({ trip_class: e.target.value })}
                  data-testid={`${prefix}-class`} className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15">
            <option value="local_oneway">Local one-way</option>
            <option value="roundtrip_under10">Round trip &lt;10 mi</option>
            <option value="dialysis_rt">Dialysis round trip</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-brand-deep block mb-1">Mobility</label>
          <select value={v.mobility} onChange={(e) => set({ mobility: e.target.value })}
                  data-testid={`${prefix}-mobility`} className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15">
            <option value="ambulatory">Ambulatory</option>
            <option value="wheelchair">Wheelchair</option>
            <option value="stretcher">Stretcher</option>
            <option value="assist_only">Assist only</option>
          </select>
        </div>
      </div>
    </>
  );
}
